import type { StopExecutionAction } from '../../executionOperations/types';
import type { ExecutionProgressHydration } from '../../executionOperations/types';
import type { EnrichedExecutionStop } from '../../executionOperations/types';
import { isLoadingStop } from '../../executionOperations/executionHelpers';
import {
  getExecutionStatusLabel,
  isInProgressOperationalState,
  isPendingOperationalState,
  normalizeOperationalState,
  type OperationalExecutionState
} from './statusFacade';
import type { RouteOperationalStatus } from './routeOperationalStatus';
import type { ActiveExecutionStopView } from './operationalContextFacade';
import { isAdvancedExecutionMode } from './workflowModeFacade';

export type OperationalExecutionActionKey =
  | 'start_loading'
  | 'complete_loading'
  | 'dispatch_transit'
  | 'start_unloading'
  | 'complete_unloading'
  | 'complete_route';

export type SimpleOperationalActionKey =
  | 'loaded'
  | 'in_transit'
  | 'unloaded'
  | 'partial_unloaded'
  | 'complete_route'
  | 'report_problem';

export type OperationalActionDescriptor = {
  key: OperationalExecutionActionKey | SimpleOperationalActionKey;
  label: string;
  internalAction: StopExecutionAction | null;
  executionStopUid?: string;
  disabled?: boolean;
  hint?: string;
  /** Simple mode: run start+complete in one confirmation. */
  compositeActions?: StopExecutionAction[];
  opensIncident?: boolean;
  requiresPartialQty?: boolean;
  mode: 'simple' | 'advanced';
};

const OPERATIONAL_ACTION_LABELS: Record<OperationalExecutionActionKey, string> = {
  start_loading: 'Начать погрузку',
  complete_loading: 'Завершить погрузку',
  dispatch_transit: 'Отправить в путь',
  start_unloading: 'Начать выгрузку',
  complete_unloading: 'Завершить выгрузку',
  complete_route: 'Завершить маршрут'
};

const SIMPLE_ACTION_LABELS: Record<SimpleOperationalActionKey, string> = {
  loaded: 'Погружено',
  in_transit: 'В пути',
  unloaded: 'Выгружено',
  partial_unloaded: 'Частично выгружено',
  complete_route: 'Завершить маршрут',
  report_problem: 'Проблема в пути'
};

export function getOperationalActionLabel(key: OperationalExecutionActionKey): string {
  return OPERATIONAL_ACTION_LABELS[key];
}

export function getSimpleActionLabel(key: SimpleOperationalActionKey): string {
  return SIMPLE_ACTION_LABELS[key];
}

export function mapOperationalActionToInternal(
  action: OperationalExecutionActionKey
): StopExecutionAction | null {
  switch (action) {
    case 'start_loading':
      return 'startLoading';
    case 'complete_loading':
      return 'completeLoading';
    case 'start_unloading':
      return 'startUnloading';
    case 'complete_unloading':
      return 'completeUnloading';
    case 'dispatch_transit':
    case 'complete_route':
      return null;
    default:
      return null;
  }
}

export function availableOperationalActions(params: {
  hydration: ExecutionProgressHydration;
  activeStop: ActiveExecutionStopView | null;
  routeStatus: RouteOperationalStatus;
  enrichedStops: EnrichedExecutionStop[];
  forceAdvanced?: boolean;
  completionLocked?: boolean;
}): OperationalActionDescriptor[] {
  const advanced = params.forceAdvanced ?? isAdvancedExecutionMode();
  if (advanced) {
    return availableAdvancedActions({ ...params, completionLocked: params.completionLocked });
  }
  return availableSimpleActions({ ...params, completionLocked: params.completionLocked });
}

function availableSimpleActions(params: {
  hydration: ExecutionProgressHydration;
  activeStop: ActiveExecutionStopView | null;
  routeStatus: RouteOperationalStatus;
  enrichedStops: EnrichedExecutionStop[];
  completionLocked?: boolean;
}): OperationalActionDescriptor[] {
  const { activeStop, routeStatus, hydration } = params;
  const actions: OperationalActionDescriptor[] = [];

  actions.push({
    key: 'report_problem',
    label: SIMPLE_ACTION_LABELS.report_problem,
    internalAction: null,
    opensIncident: true,
    mode: 'simple'
  });

  if (activeStop) {
    const { stop, operationalState } = activeStop;
    const isLoading = isLoadingStop(stop.type);

    if (isLoading) {
      if (operationalState === 'loading_pending') {
        actions.push({
          key: 'loaded',
          label: SIMPLE_ACTION_LABELS.loaded,
          internalAction: null,
          executionStopUid: stop.executionStopUid,
          compositeActions: ['startLoading', 'completeLoading'],
          mode: 'simple'
        });
      } else if (operationalState === 'loading_in_progress' || operationalState === 'partially_delivered') {
        actions.push({
          key: 'loaded',
          label: SIMPLE_ACTION_LABELS.loaded,
          internalAction: 'completeLoading',
          executionStopUid: stop.executionStopUid,
          mode: 'simple'
        });
      }
    } else if (routeStatus.allLoadingComplete) {
      if (operationalState === 'unloading_pending') {
        actions.push({
          key: 'unloaded',
          label: SIMPLE_ACTION_LABELS.unloaded,
          internalAction: null,
          executionStopUid: stop.executionStopUid,
          compositeActions: ['startUnloading', 'completeUnloading'],
          mode: 'simple'
        });
      } else if (operationalState === 'unloading_in_progress' || operationalState === 'partially_delivered') {
        actions.push({
          key: 'unloaded',
          label: SIMPLE_ACTION_LABELS.unloaded,
          internalAction: 'completeUnloading',
          executionStopUid: stop.executionStopUid,
          mode: 'simple'
        });
        actions.push({
          key: 'partial_unloaded',
          label: SIMPLE_ACTION_LABELS.partial_unloaded,
          internalAction: 'completeUnloading',
          executionStopUid: stop.executionStopUid,
          requiresPartialQty: true,
          mode: 'simple'
        });
      }
    }
  }

  if (routeStatus.allLoadingComplete && !routeStatus.anyUnloadingStarted) {
    actions.push({
      key: 'in_transit',
      label: SIMPLE_ACTION_LABELS.in_transit,
      internalAction: null,
      disabled: routeStatus.key === 'in_transit',
      hint:
        routeStatus.key === 'in_transit'
          ? 'Маршрут уже в пути'
          : 'Статус «В пути» устанавливается после завершения погрузки',
      mode: 'simple'
    });
  }

  if (hydration.progress.executionComplete && !params.completionLocked) {
    actions.push({
      key: 'complete_route',
      label: SIMPLE_ACTION_LABELS.complete_route,
      internalAction: null,
      disabled: false,
      hint: 'Завершить маршрут: штрафы, расходы, комментарий логиста',
      mode: 'simple'
    });
  }

  return dedupeActions(actions);
}

function availableAdvancedActions(params: {
  hydration: ExecutionProgressHydration;
  activeStop: ActiveExecutionStopView | null;
  routeStatus: RouteOperationalStatus;
  enrichedStops: EnrichedExecutionStop[];
  completionLocked?: boolean;
}): OperationalActionDescriptor[] {
  const actions: OperationalActionDescriptor[] = [];
  const { activeStop, routeStatus, enrichedStops, hydration } = params;

  if (activeStop) {
    const { stop, operationalState } = activeStop;
    const isLoading = isLoadingStop(stop.type);

    if (isLoading) {
      if (operationalState === 'loading_pending') {
        actions.push({
          key: 'start_loading',
          label: OPERATIONAL_ACTION_LABELS.start_loading,
          internalAction: 'startLoading',
          executionStopUid: stop.executionStopUid,
          mode: 'advanced'
        });
      }
      if (operationalState === 'loading_in_progress' || operationalState === 'partially_delivered') {
        actions.push({
          key: 'complete_loading',
          label: OPERATIONAL_ACTION_LABELS.complete_loading,
          internalAction: 'completeLoading',
          executionStopUid: stop.executionStopUid,
          mode: 'advanced'
        });
      }
    } else {
      if (operationalState === 'unloading_pending' && routeStatus.allLoadingComplete) {
        actions.push({
          key: 'start_unloading',
          label: OPERATIONAL_ACTION_LABELS.start_unloading,
          internalAction: 'startUnloading',
          executionStopUid: stop.executionStopUid,
          mode: 'advanced'
        });
      }
      if (operationalState === 'unloading_in_progress' || operationalState === 'partially_delivered') {
        actions.push({
          key: 'complete_unloading',
          label: OPERATIONAL_ACTION_LABELS.complete_unloading,
          internalAction: 'completeUnloading',
          executionStopUid: stop.executionStopUid,
          mode: 'advanced'
        });
      }
    }
  }

  if (
    routeStatus.allLoadingComplete &&
    !routeStatus.anyUnloadingStarted &&
    routeStatus.key === 'in_transit'
  ) {
    actions.push({
      key: 'dispatch_transit',
      label: OPERATIONAL_ACTION_LABELS.dispatch_transit,
      internalAction: null,
      disabled: true,
      hint: 'Маршрут автоматически переведён в статус «В пути» после завершения погрузки',
      mode: 'advanced'
    });
  }

  if (params.completionLocked) {
    actions.push({
      key: 'complete_route',
      label: OPERATIONAL_ACTION_LABELS.complete_route,
      internalAction: null,
      disabled: true,
      hint: 'Маршрут уже завершён',
      mode: 'advanced'
    });
  } else if (hydration.progress.executionComplete) {
    actions.push({
      key: 'complete_route',
      label: OPERATIONAL_ACTION_LABELS.complete_route,
      internalAction: null,
      disabled: false,
      hint: 'Завершить маршрут: штрафы, расходы, комментарий логиста',
      mode: 'advanced'
    });
  }

  const pendingLoading = enrichedStops.filter(
    (s) =>
      isLoadingStop(s.type) &&
      isPendingOperationalState(normalizeOperationalState(s.executionState)) &&
      s.executionStopUid !== activeStop?.stop.executionStopUid
  );
  for (const stop of pendingLoading.slice(0, 2)) {
    actions.push({
      key: 'start_loading',
      label: `${OPERATIONAL_ACTION_LABELS.start_loading}: ${stop.label}`,
      internalAction: 'startLoading',
      executionStopUid: stop.executionStopUid,
      mode: 'advanced'
    });
  }

  const pendingUnloading = enrichedStops.filter(
    (s) =>
      !isLoadingStop(s.type) &&
      isPendingOperationalState(normalizeOperationalState(s.executionState)) &&
      routeStatus.allLoadingComplete &&
      s.executionStopUid !== activeStop?.stop.executionStopUid
  );
  for (const stop of pendingUnloading.slice(0, 2)) {
    actions.push({
      key: 'start_unloading',
      label: `${OPERATIONAL_ACTION_LABELS.start_unloading}: ${stop.label}`,
      internalAction: 'startUnloading',
      executionStopUid: stop.executionStopUid,
      mode: 'advanced'
    });
  }

  const inProgressOthers = enrichedStops.filter(
    (s) =>
      isInProgressOperationalState(normalizeOperationalState(s.executionState)) &&
      s.executionStopUid !== activeStop?.stop.executionStopUid
  );
  for (const stop of inProgressOthers) {
    if (isLoadingStop(stop.type)) {
      actions.push({
        key: 'complete_loading',
        label: `${OPERATIONAL_ACTION_LABELS.complete_loading}: ${stop.label}`,
        internalAction: 'completeLoading',
        executionStopUid: stop.executionStopUid,
        mode: 'advanced'
      });
    } else {
      actions.push({
        key: 'complete_unloading',
        label: `${OPERATIONAL_ACTION_LABELS.complete_unloading}: ${stop.label}`,
        internalAction: 'completeUnloading',
        executionStopUid: stop.executionStopUid,
        mode: 'advanced'
      });
    }
  }

  if (hydration.progress.executionComplete && actions.length === 0 && !params.completionLocked) {
    actions.push({
      key: 'complete_route',
      label: OPERATIONAL_ACTION_LABELS.complete_route,
      internalAction: null,
      disabled: false,
      hint: 'Завершить маршрут: штрафы, расходы, комментарий логиста',
      mode: 'advanced'
    });
  }

  actions.push({
    key: 'report_problem',
    label: SIMPLE_ACTION_LABELS.report_problem,
    internalAction: null,
    opensIncident: true,
    mode: 'advanced'
  });

  return dedupeActions(actions);
}

function dedupeActions(actions: OperationalActionDescriptor[]): OperationalActionDescriptor[] {
  const seen = new Set<string>();
  return actions.filter((a) => {
    const key = `${a.key}:${a.executionStopUid ?? 'route'}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function availableOperationalActionsForStop(
  stop: EnrichedExecutionStop,
  routeStatus: RouteOperationalStatus,
  hydration: ExecutionProgressHydration,
  enrichedStops: EnrichedExecutionStop[],
  forceAdvanced?: boolean
): OperationalActionDescriptor[] {
  const activeStop: ActiveExecutionStopView = {
    stop,
    operationalState: normalizeOperationalState(stop.executionState) as OperationalExecutionState,
    typeLabel: isLoadingStop(stop.type) ? 'Погрузка' : 'Выгрузка',
    statusLabel: getExecutionStatusLabel(normalizeOperationalState(stop.executionState)),
    address: stop.address,
    plannedAt: stop.plannedAt ?? null,
    actualAt: isLoadingStop(stop.type)
      ? stop.loadingCompletedAt ?? stop.loadingStartedAt ?? stop.actualAt ?? null
      : stop.unloadingCompletedAt ?? stop.unloadingStartedAt ?? stop.actualAt ?? null,
    cargoPlanned: stop.cargoPlanned,
    cargoLoaded: stop.cargoLoaded,
    cargoUnloaded: stop.cargoUnloaded,
    cargoRemaining: isLoadingStop(stop.type)
      ? Math.max(0, stop.cargoPlanned - stop.cargoLoaded)
      : Math.max(0, stop.cargoPlanned - stop.cargoUnloaded),
    progressPercent: stop.completionPercent,
    context: {
      applicationNumbers: [],
      applicationLabel: null,
      clientName: null,
      consigneeName: null,
      warehouseName: null,
      cargoLines: [],
      loadedLines: [],
      remainingLines: [],
      totalPlaces: 0,
      totalWeightKg: 0,
      plannedAt: stop.plannedAt ?? null,
      actualAt: isLoadingStop(stop.type)
        ? stop.loadingCompletedAt ?? stop.loadingStartedAt ?? stop.actualAt ?? null
        : stop.unloadingCompletedAt ?? stop.unloadingStartedAt ?? stop.actualAt ?? null
    }
  };

  return availableOperationalActions({
    hydration,
    activeStop,
    routeStatus,
    enrichedStops,
    forceAdvanced
  }).filter(
    (a) => a.opensIncident || a.key === 'complete_route' || a.executionStopUid === stop.executionStopUid
  );
}
